const SHOPIFY_CHANNEL_TYPE = 'shopify';

export const isShopifySalesChannelId = (channelId, salesChannels = []) => {
  if (!channelId) return false;
  const teamChannel = salesChannels.find(
    (channel) => String(channel._id) === String(channelId),
  );
  if (teamChannel) return teamChannel.type === SHOPIFY_CHANNEL_TYPE;
  return String(channelId) === SHOPIFY_CHANNEL_TYPE;
};

export const getShopifyListingsFromTargets = (targets = {}, salesChannels = []) => (
  (targets.listings || []).filter((listing) => (
    isShopifySalesChannelId(listing.channelId, salesChannels)
  ))
);

/**
 * Sync channel option is Shopify when type is shopify, or value is the
 * legacy channel id string "shopify".
 */
export const isShopifyChannelOption = (option = {}) => (
  option.type === SHOPIFY_CHANNEL_TYPE
  || String(option.value) === SHOPIFY_CHANNEL_TYPE
);

/**
 * Keeps only per-channel media maps that belong to Shopify sales channels.
 */
export const pickShopifyMediaByChannel = (mediaByChannel = {}, channelOptions = []) => {
  const shopifyIds = new Set(
    (channelOptions || [])
      .filter(isShopifyChannelOption)
      .map(({ value }) => String(value)),
  );
  return Object.fromEntries(
    Object.entries(mediaByChannel || {})
      .filter(([channelId]) => shopifyIds.has(String(channelId))),
  );
};

export const resolveMasterVariantIdFromListingVariant = (
  listingVariantId,
  productsState,
) => {
  if (!listingVariantId) return null;
  const fromEntity = productsState?.entities?.listingVariants?.[listingVariantId]
    ?.masterVariantId;
  if (fromEntity) return String(fromEntity);
  return null;
};

/**
 * Master layers that would mirror the selected Shopify listing targets.
 */
export const computeDesiredMasterMirror = ({
  targets = {},
  productsState,
  salesChannels = [],
} = {}) => {
  const shopifyListings = getShopifyListingsFromTargets(targets, salesChannels);
  const masterVariantIds = [];
  const seen = new Set();

  shopifyListings.forEach((listing) => {
    (listing.listingVariantIds || []).forEach((listingVariantId) => {
      const masterVariantId = resolveMasterVariantIdFromListingVariant(
        listingVariantId,
        productsState,
      );
      if (!masterVariantId || seen.has(masterVariantId)) return;
      seen.add(masterVariantId);
      masterVariantIds.push(masterVariantId);
    });
  });

  return {
    masterGallery: shopifyListings.some((listing) => listing.listingGallery),
    masterVariantIds,
  };
};

export const hasAnyMasterSelection = (targets = {}) => (
  Boolean(targets.masterGallery) || Boolean(targets.masterVariantIds?.length)
);

export const hasMirrorableMasterTargets = (desired = {}) => (
  Boolean(desired.masterGallery) || Boolean(desired.masterVariantIds?.length)
);

export const assetHasMasterGallery = (asset, masterProductId) => {
  if (!asset || !masterProductId) return false;
  return (asset.products || []).some((link) => {
    if (String(link._id) !== String(masterProductId)) return false;
    if (link.type === 'listing') return false;
    return link.type === 'master' || !link.type;
  });
};

export const assetHasMasterVariant = (asset, masterVariantId) => {
  if (!asset || !masterVariantId) return false;
  return (asset.productVariants || []).some((link) => (
    String(link.variantId) === String(masterVariantId)
    && link.type === 'masterVariant'
  ));
};

export const assetMissesDesiredMaster = (asset, desired, masterProductId) => {
  if (desired.masterGallery && !assetHasMasterGallery(asset, masterProductId)) {
    return true;
  }
  return (desired.masterVariantIds || []).some(
    (variantId) => !assetHasMasterVariant(asset, variantId),
  );
};

export const anyAssetMissesDesiredMaster = (
  assets = [],
  desired,
  masterProductId,
) => assets.some((asset) => assetMissesDesiredMaster(asset, desired, masterProductId));

export const applyMasterMirrorToTargets = (targets = {}, desired = {}) => {
  const existingVariantIds = targets.masterVariantIds || [];
  const mergedVariantIds = [...existingVariantIds];
  const seen = new Set(existingVariantIds.map(String));

  (desired.masterVariantIds || []).forEach((variantId) => {
    const key = String(variantId);
    if (seen.has(key)) return;
    seen.add(key);
    mergedVariantIds.push(variantId);
  });

  return {
    ...targets,
    masterGallery: Boolean(targets.masterGallery || desired.masterGallery),
    masterVariantIds: mergedVariantIds,
  };
};

/**
 * Whether the Master consistency modal should be shown for this confirm.
 */
export const shouldShowListingMasterConsistency = ({
  targets,
  assets = [],
  masterProductId,
  productsState,
  salesChannels = [],
  dontShowAgain = false,
} = {}) => {
  if (dontShowAgain) return false;
  if (hasAnyMasterSelection(targets)) return false;

  const desired = computeDesiredMasterMirror({
    targets,
    productsState,
    salesChannels,
  });

  if (!hasMirrorableMasterTargets(desired)) return false;
  if (!assets.length) return false;

  return anyAssetMissesDesiredMaster(assets, desired, masterProductId);
};
